import { useState, useEffect } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Database, Layers, Radio, HardDrive, Plug, Play, Check, X, Loader2, Trash2, Cloud, Cpu, Wind, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const categoryIcons = { database: Database, pipeline: Layers, streaming: Radio, storage: HardDrive };
const categoryColors = { database: "#29B5E8", pipeline: "#8B5CF6", streaming: "#22C55E", storage: "#F59E0B" };

const typeLabels = {
  postgresql: "PostgreSQL", mysql: "MySQL", snowflake: "Snowflake", bigquery: "BigQuery",
  airflow: "Apache Airflow", dbt: "dbt Cloud", fivetran: "Fivetran", dagster: "Dagster",
  kafka: "Apache Kafka", kinesis: "AWS Kinesis", s3: "Amazon S3", gcs: "Google Cloud Storage",
};

export default function IntegrationsPage() {
  const { user } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ project_id: "", name: "", type: "", config: {} });
  const isAdmin = user?.role === "admin";

  const fetchAll = async () => {
    try {
      const [intRes, projRes, typeRes] = await Promise.all([api.get("/integrations"), api.get("/projects"), api.get("/integrations/types")]);
      setIntegrations(intRes.data);
      setProjects(projRes.data);
      setTypes(typeRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    try {
      await api.post("/integrations", form);
      fetchAll();
      setShowCreate(false);
      setForm({ project_id: "", name: "", type: "", config: {} });
      toast.success("Integration created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleTest = async (integrationId) => {
    setTesting(integrationId); setTestResult(null);
    try {
      const { data } = await api.post(`/integrations/${integrationId}/test`);
      setTestResult({ id: integrationId, ...data.test_result });
      fetchAll();
      toast.success("Connection test passed");
    } catch { toast.error("Test failed"); setTestResult({ id: integrationId, connected: false }); }
    finally { setTesting(null); }
  };

  const handleDelete = async (integrationId) => {
    try { await api.delete(`/integrations/${integrationId}`); fetchAll(); toast.success("Integration removed"); } catch { toast.error("Failed"); }
  };

  const selectedTypeFields = types[form.type]?.fields || [];

  if (loading) return <div className="p-6 space-y-4" data-testid="integrations-loading"><Skeleton className="h-8 w-48 bg-white/5" />{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 bg-white/5 rounded-xl" />)}</div>;

  const grouped = {};
  integrations.forEach((i) => { const cat = i.category || "other"; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(i); });

  return (
    <div className="p-6 space-y-6" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Integrations</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>{integrations.length} data source connections</p>
        </div>
        {isAdmin && (
          <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={() => setShowCreate(true)} data-testid="add-integration-button">
            <Plus className="w-4 h-4 mr-2" /> Add Integration
          </Button>
        )}
      </div>

      {/* Grouped by category */}
      {["database", "pipeline", "streaming", "storage"].map((cat) => {
        const items = grouped[cat];
        if (!items?.length) return null;
        const CatIcon = categoryIcons[cat];
        const catColor = categoryColors[cat];
        return (
          <div key={cat} className="space-y-3" data-testid={`category-${cat}`}>
            <div className="flex items-center gap-2">
              <CatIcon className="w-4 h-4" style={{ color: catColor }} />
              <h2 className="text-[13px] uppercase tracking-[0.12em] font-bold" style={{ color: catColor }}>{cat === "database" ? "Databases" : cat === "pipeline" ? "Pipeline Tools" : cat === "streaming" ? "Streaming" : "Storage"}</h2>
              <Badge className="text-[9px] font-mono border-0 rounded-md" style={{ background: `${catColor}12`, color: catColor }}>{items.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((intg) => {
                const projectName = projects.find((p) => p.project_id === intg.project_id)?.name || intg.project_id;
                const isTestingThis = testing === intg.integration_id;
                const thisResult = testResult?.id === intg.integration_id ? testResult : null;
                return (
                  <div key={intg.integration_id} className="sf-card p-4" data-testid={`integration-card-${intg.integration_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${catColor}12` }}>
                          <CatIcon className="w-4 h-4" style={{ color: catColor }} />
                        </div>
                        <div>
                          <h3 className="text-[13px] font-bold text-[#2D3142]">{intg.name}</h3>
                          <p className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{typeLabels[intg.type] || intg.type} / {projectName}</p>
                        </div>
                      </div>
                      <Badge className="text-[8px] uppercase font-bold border-0 rounded-md" style={{ background: intg.status === "connected" ? "rgba(45,106,79,0.12)" : "rgba(255,255,255,0.05)", color: intg.status === "connected" ? "#4ADE80" : "#5A6B82" }}>{intg.status}</Badge>
                    </div>
                    {/* Config display */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {Object.entries(intg.config_display || {}).slice(0, 4).map(([k, v]) => (
                        <span key={k} className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: "rgba(45,49,66,0.03)", color: "#6A7B8E" }}>{k}: {String(v).slice(0, 25)}</span>
                      ))}
                    </div>
                    {/* Test result */}
                    {intg.last_test_result && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {Object.entries(intg.last_test_result).filter(([k]) => k !== "connected").map(([k, v]) => (
                          <span key={k} className="text-[9px] font-mono" style={{ color: "#4A5A70" }}>{k.replace(/_/g, " ")}: <span className="text-[#8899AA]">{v}</span></span>
                        ))}
                      </div>
                    )}
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="h-7 rounded-lg text-[10px] font-semibold text-[#2D3142] border-0" style={{ background: "rgba(30,58,95,0.15)", color: "#1E3A5F" }} onClick={() => handleTest(intg.integration_id)} disabled={isTestingThis} data-testid={`test-integration-${intg.integration_id}`}>
                        {isTestingThis ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                        {isTestingThis ? "Testing..." : "Test Connection"}
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 rounded-lg text-[10px] text-[#5A6B82] hover:text-[#EF4444] hover:bg-[#EF4444]/10" onClick={() => handleDelete(intg.integration_id)} data-testid={`delete-integration-${intg.integration_id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      )}
                      {intg.last_tested && <span className="text-[9px] font-mono ml-auto" style={{ color: "#9CA3AF" }}>Last: {intg.last_tested?.slice(0, 16).replace("T", " ")}</span>}
                    </div>
                    {/* Live test result */}
                    {thisResult && (
                      <div className="mt-2 p-2 rounded-lg" style={{ background: thisResult.connected ? "rgba(45,106,79,0.06)" : "rgba(190,52,85,0.06)", border: `1px solid ${thisResult.connected ? "rgba(45,106,79,0.15)" : "rgba(190,52,85,0.15)"}` }}>
                        <div className="flex items-center gap-1.5">
                          {thisResult.connected ? <Check className="w-3 h-3" style={{ color: "#2D6A4F" }} /> : <X className="w-3 h-3" style={{ color: "#BE3455" }} />}
                          <span className="text-[10px] font-bold" style={{ color: thisResult.connected ? "#22C55E" : "#EF4444" }}>{thisResult.connected ? "Connected" : "Failed"}</span>
                          {thisResult.latency_ms && <span className="text-[9px] font-mono ml-auto" style={{ color: "#6B7280" }}>{thisResult.latency_ms}ms</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[520px] max-h-[80vh] overflow-auto" style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }} data-testid="create-integration-dialog">
          <DialogHeader><DialogTitle className="text-[#2D3142]">Add Integration</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="integration-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
                  {projects.filter((p) => p.is_active).map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Integration Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, config: {} })}>
                <SelectTrigger className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="integration-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent style={{ background: "#FFFFFF", border: "1px solid #E8E4DF" }}>
                  {Object.entries(types).map(([key, info]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: categoryColors[info.category] }} />
                        {typeLabels[key] || key}
                        <span className="text-[9px] ml-1" style={{ color: "#6B7280" }}>({info.category})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-semibold" style={{ color: "#6B7280" }}>Display Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Production Snowflake" className="h-10 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142]" data-testid="integration-name-input" />
            </div>
            {selectedTypeFields.length > 0 && (
              <div className="space-y-3 pt-2">
                <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>Connection Details</p>
                {selectedTypeFields.map((field) => (
                  <div key={field} className="space-y-1">
                    <Label className="text-[10px] font-medium capitalize" style={{ color: "#6B7280" }}>{field.replace(/_/g, " ")}</Label>
                    <Input
                      type={field.includes("password") || field.includes("secret") || field.includes("credentials") ? "password" : "text"}
                      value={form.config[field] || ""} onChange={(e) => setForm({ ...form, config: { ...form.config, [field]: e.target.value } })}
                      placeholder={field} className="h-9 rounded-lg bg-black/[0.02] border-gray-200 text-[#2D3142] text-[12px] font-mono" data-testid={`config-field-${field}`} />
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-lg border-gray-200" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="rounded-lg font-semibold text-sm border-0 text-[#2D3142]" style={{ background: "linear-gradient(135deg, #29B5E8 0%, #1B8FC2 100%)" }} onClick={handleCreate} disabled={!form.name || !form.type || !form.project_id} data-testid="submit-integration-button">
                <Plug className="w-3.5 h-3.5 mr-1.5" /> Create Integration
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
